import { useState } from 'react';
import { TagPicker, type PickerTag } from '@/components/ui/TagPicker';
import { TagPill } from '@/components/ui/TagPill';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';
import { useCreateTag, useSetEntityTags, type TaggableType } from '@/lib/api/tags';

interface Props {
  companyId: number;
  entityType: TaggableType;
  entityId: number;
  /** El vocabulario de la marca. Lo carga la pantalla, una vez para toda la lista. */
  vocabulario: PickerTag[];
  /** Las que lleva esta entidad, de la lectura en bloque de la pantalla. */
  valor: PickerTag[];
  /** Sin permiso de edición se pintan las píldoras y ya. */
  readOnly?: boolean;
  className?: string;
}

/**
 * Las etiquetas de UNA entidad, listas para poner y quitar.
 *
 * Va dentro de la fila de la lista y no en una pantalla de detalle a propósito:
 * etiquetar es una tarea de tanda —se marcan diez eventos seguidos— y obligar a
 * entrar y salir de cada ficha para poner una palabra convierte cinco minutos
 * de trabajo en veinte.
 *
 * **El vocabulario y las asignaciones llegan por props.** Si cada fila los
 * pidiera por su cuenta, una lista de cien estaciones haría cien peticiones y
 * las píldoras aparecerían a goteo. La pantalla los pide una vez en bloque y
 * los reparte.
 *
 * Guarda al vuelo: no hay botón de guardar porque no hay formulario que
 * confirmar — marcar es la acción, y esperar a un «guardar» que no se ve al
 * final de una tabla larga es como se pierden los cambios.
 */
export function EntityTags({
  companyId,
  entityType,
  entityId,
  vocabulario,
  valor,
  readOnly,
  className,
}: Props) {
  const t = useT();
  const crear = useCreateTag(companyId, entityType);
  const guardar = useSetEntityTags(companyId, entityType);

  /*
    Copia local para que la píldora responda al instante.

    El guardado va al servidor y la lista se revalida después; sin este eco
    inmediato, entre el clic y la respuesta la etiqueta parece no haberse
    puesto y el usuario vuelve a pulsar.
  */
  const [eco, setEco] = useState<PickerTag[] | null>(null);
  const actuales = eco ?? valor;

  if (readOnly) {
    if (actuales.length === 0) return null;
    return (
      <div className={className}>
        <div className="flex flex-wrap gap-1">
          {actuales.map((tag) => (
            <TagPill key={tag.id} name={tag.name} color={tag.color} size="sm" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <TagPicker
      className={className}
      vocabulario={vocabulario}
      valor={actuales}
      onChange={(tags) => {
        setEco(tags);
        guardar.mutate(
          { entityId, tagIds: tags.map((x) => x.id) },
          {
            onError: (e: Error) => {
              // Se deshace el eco: dejar la píldora puesta después de un fallo
              // haría creer que quedó guardada.
              setEco(null);
              toast({ tone: 'error', title: t('tags.saveFailed'), description: e.message });
            },
            // Al terminar se suelta el eco y manda lo que diga el servidor.
            onSuccess: () => setEco(null),
          },
        );
      }}
      onCrear={async (name, color) => {
        const nueva = await crear.mutateAsync({ name, color });
        return { id: nueva.id, name: nueva.name, color: nueva.color };
      }}
    />
  );
}
